import type { FC } from 'react'
import type { HistoryItem, SceneProps } from '@/components/landing/games/scene-ui'

import { useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { formatMultiplier } from '@/lib/format'
import { useSequence } from '@/hooks/use-sequence'
import CountUp from '@/components/landing/count-up'
import { HistoryChip, SceneBadge, SceneField } from '@/components/landing/games/scene-ui'

const TARGET = 2
const SCALE = [1, 2, 5, 10, 50, 100]

/** Position on a log ruler from 1x at the bottom to 100x at the top, in percent from the top. */
const rulerTop = (value: number): number => 100 - (Math.log10(Math.min(Math.max(value, 1), 100)) / 2) * 100

const POSTER_HISTORY: HistoryItem[] = [
    { id: 4, value: 1.24 },
    { id: 3, value: 5.02 },
    { id: 2, value: 1.07 },
    { id: 1, value: 12.4 }
]

const sampleResult = (): number => {
    const raw = 0.99 / (1 - Math.random())
    return Math.min(1000000, Math.max(1, Math.floor(raw * 100) / 100))
}

const LimboScene: FC<SceneProps> = ({ active }) => {
    const [result, setResult] = useState(3.71)
    const [round, setRound] = useState(0)
    const [settled, setSettled] = useState(true)
    const [history, setHistory] = useState<HistoryItem[]>(POSTER_HISTORY)
    const nextId = useRef(5)

    useSequence(active, async ({ wait }) => {
        const value = sampleResult()

        setSettled(false)
        setResult(value)
        setRound(previous => previous + 1)

        if (!(await wait(950))) return

        setSettled(true)
        const id = nextId.current
        nextId.current += 1
        setHistory(previous => [{ id, value }, ...previous].slice(0, 4))

        await wait(1500)
    })

    const win = result >= TARGET

    return (
        <div className='absolute inset-0 flex gap-5 px-6 pb-6 pt-14'>
            <div className='flex min-w-0 flex-1 flex-col justify-center'>
                <div className='flex items-baseline gap-1 font-mono leading-none'>
                    <span className={cn('text-2xl transition-colors duration-300', settled && win ? 'text-ember' : 'text-bone/40')}>×</span>
                    <CountUp
                        value={result}
                        from={1}
                        replay={round}
                        duration={900}
                        easing='expo'
                        format={formatMultiplier}
                        className={cn('truncate text-[44px] font-medium tabular-nums tracking-tight transition-colors duration-300', settled ? (win ? 'ember-glow text-ember' : 'text-bone/40') : 'text-bone')}
                    />
                </div>

                <div className='mt-3'>
                    {settled
                        ? <SceneBadge tone={win ? 'ember' : 'muted'}>{win ? 'Reached target' : 'Below target'}</SceneBadge>
                        : <SceneBadge tone='default'>Climbing</SceneBadge>}
                </div>

                <div className='mt-5 grid grid-cols-2 gap-2'>
                    <SceneField label='Target' value='×2.00' />
                    <SceneField label='Win chance' value='49.50%' />
                </div>

                <div className='mt-4 flex h-5 gap-1 overflow-hidden'>
                    {history.map(item => (
                        <HistoryChip key={item.id} hot={item.value >= TARGET}>
                            {formatMultiplier(item.value)}×
                        </HistoryChip>
                    ))}
                </div>
            </div>

            <div aria-hidden='true' className='relative w-12 shrink-0 border-l border-white/[0.08]'>
                {SCALE.map(mark => (
                    <div key={mark} className='absolute left-0 flex -translate-y-1/2 items-center gap-1.5' style={{ top: `${rulerTop(mark)}%` }}>
                        <span className='h-px w-2.5 bg-white/25' />
                        <span className='font-mono text-[9px] text-steel/70'>{mark}×</span>
                    </div>
                ))}

                <div className='absolute left-0 w-full border-t border-dashed border-bone/40' style={{ top: `${rulerTop(TARGET)}%` }} />

                <div
                    className='absolute -left-1 w-[calc(100%+4px)] transition-[top] duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)]'
                    style={{ top: `${rulerTop(result)}%` }}
                >
                    <div className='h-0.5 w-full -translate-y-1/2 rounded-full bg-ember shadow-[0_0_14px_2px_rgba(255,91,31,0.7)]' />
                </div>
            </div>
        </div>
    )
}

export default LimboScene
